import React, { useEffect, useState } from 'react'
import { useCatalogStore } from '../../stores/catalogStore'
import { useCatalog } from '../../hooks/useCatalog'
import FacetGroup from './FacetGroup'
import InitiativeFacet from './InitiativeFacet'

// Left sidebar listing every CatalogDimension's facet group. Loads the
// taxonomy once on mount from GET /api/v1/catalog/dimensions; facet counts
// are read from the current store.facets (which is populated by whatever
// fetchEntries() returned).
//
// Variants:
//   "sidebar" (default) — fixed ~288px sidebar, hidden below `lg`
//   "drawer"            — renders inside a mobile slide-over (full width,
//                         always visible)
//
// "Clear all filters" link at the top appears only when any filter is set.
export default function FacetPanel({ variant = 'sidebar' }) {
  const { fetchDimensions } = useCatalog()
  const [dimensions, setDimensions] = useState([])
  const [loading,    setLoading]    = useState(true)
  const [error,      setError]      = useState(null)

  const facets             = useCatalogStore((s) => s.facets)
  const activeFilters      = useCatalogStore((s) => s.filters)
  const toggleDimensionValue = useCatalogStore((s) => s.toggleDimensionValue)
  const clearAllFilters    = useCatalogStore((s) => s.clearAllFilters)
  const fetchEntries       = useCatalogStore((s) => s.fetchEntries)

  useEffect(() => {
    let cancelled = false
    fetchDimensions()
      .then((data) => {
        if (!cancelled) setDimensions(data || [])
      })
      .catch((err) => !cancelled && setError(err.message))
      .finally(() => !cancelled && setLoading(false))
    return () => { cancelled = true }
  }, [fetchDimensions])

  const hasAnyFilter =
    Object.values(activeFilters.dimensions || {}).some((v) => v && v.length > 0) ||
    activeFilters.entry_type ||
    (activeFilters.search && activeFilters.search.length > 0) ||
    activeFilters.initiative_id ||
    activeFilters.person_id

  const handleClearAll = () => {
    clearAllFilters()
    fetchEntries()
  }

  const handleToggle = (dimensionSlug, valueSlug) => {
    toggleDimensionValue(dimensionSlug, valueSlug)
    fetchEntries()
  }

  const wrapperClass =
    variant === 'drawer'
      ? 'w-full bg-white'
      : 'hidden lg:block w-72 shrink-0 border-r border-slate-200 bg-white max-h-[calc(100vh-8rem)] overflow-y-auto'

  return (
    <aside className={wrapperClass} aria-label="Catalog filters">
      <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900">Filters</h3>
        {hasAnyFilter && (
          <button
            type="button"
            onClick={handleClearAll}
            className="text-xs font-medium text-accent-600 hover:text-accent-500"
          >
            Clear all
          </button>
        )}
      </div>

      <div className="px-5">
        {/* Phase 3: Initiative facet sits at the top, above the dimension
            groups (decision §6.3). It loads its own options independently. */}
        <InitiativeFacet />

        {loading && <p className="py-4 text-xs text-slate-400">Loading facets…</p>}
        {error && <p className="py-4 text-xs text-rose-600">{error}</p>}

        {!loading && !error && dimensions.map((dim) => {
          // Use facets from the current result set; fall back to the full
          // dimension.values list (with count 0) when a dimension has no
          // matching entries under the current filter set.
          const facetValues = facets[dim.slug] || []
          const facetBySlug = facetValues.reduce((acc, v) => {
            acc[v.slug] = v
            return acc
          }, {})

          const mergedValues = (dim.values || []).map((v) => ({
            id:    v.id,
            name:  v.name,
            slug:  v.slug,
            count: facetBySlug[v.slug]?.count ?? 0,
          }))

          return (
            <FacetGroup
              key={dim.id}
              dimension={dim}
              values={mergedValues}
              selectedSlugs={activeFilters.dimensions?.[dim.slug] || []}
              onToggle={(valueSlug) => handleToggle(dim.slug, valueSlug)}
            />
          )
        })}
      </div>
    </aside>
  )
}
